'use client';
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import PublicHeader from '@/components/PublicHeader';
import PublicFooter from '@/components/PublicFooter';
import { useT, LANG_STORAGE_KEY } from '@/lib/useT';
import { useAuthStore } from '@/store/auth.store';

interface CmsSection { id: string; title: string; body: string }
interface CmsPage { title: string; subtitle?: string; sections: CmsSection[]; updatedAt?: string }

interface Props {
  slug: 'about' | 'impressum' | 'datenschutz' | 'agb' | 'docs'
      | 'platform-funktionen' | 'platform-backoffice' | 'platform-ki' | 'platform-integrationen'
      | 'kunden-marktplaetze' | 'kunden-portal';
  /** Inline fallback used when the CMS page hasn't been edited yet */
  fallback: CmsPage;
  /** Per-language fallbacks (keys: de/en/ru/ua/pl/cz/md/bg/kz). When present,
   *  the entry matching the active language is shown instead of `fallback`. */
  fallbacks?: Record<string, CmsPage>;
}

/** Map app-level lang code (e.g. "DE", "EN") to the API's lower-case slug. */
function apiLang(): string {
  if (typeof window === 'undefined') return 'de';
  const stored = (() => { try { return localStorage.getItem(LANG_STORAGE_KEY); } catch { return null; } })();
  const code = (stored || (navigator?.language?.slice(0, 2)) || 'de').toLowerCase();
  // App uses RU/DE/UA/MD/KZ/CZ/PL/BG/EN — API uses ua/md/kz/cz instead of UK/RO/etc.
  // Direct mapping since both sides use the same 2-letter codes lowercase.
  const map: Record<string, string> = {
    de: 'de', en: 'en', ru: 'ru', ua: 'ua', uk: 'ua', md: 'md', ro: 'md',
    kz: 'kz', kk: 'kz', cz: 'cz', cs: 'cz', pl: 'pl', bg: 'bg',
  };
  return map[code] || 'de';
}

/**
 * Renders a long-form CMS page (Über uns, Impressum, Datenschutz, AGB)
 * with inline-defaults until the API returns a saved override. Body
 * text is rendered as lightweight markdown (paragraphs, **bold**, *italic*,
 * - bullets, links). The structure is intentionally simple so super-admin
 * can paste plain text or pasted-from-Word content without breaking.
 *
 * Language: prefers logged-in user's profile language, falls back to
 * `logixa_lang` localStorage, then to navigator.language, finally DE.
 */
export default function CmsPageRenderer({ slug, fallback, fallbacks }: Props) {
  const user = useAuthStore(s => s.user);
  // SSR + first client paint must render the SAME language or React throws a
  // hydration mismatch (and re-renders the whole root) on every legal page.
  // The server resolves to 'de' (apiLang() has no window, zustand user is empty),
  // so we hold 'de' until mount, then switch to the visitor's real language.
  // (audit WEB-001, 2026-06-02)
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);
  // user.language is upper-case (DE/EN/RU/...) — API expects lowercase + UA/MD/KZ/CZ slugs.
  const lang = mounted ? ((user?.language || '').toLowerCase() || apiLang()) : 'de';
  const localized = (fallbacks && fallbacks[lang]) || fallback;
  const [page, setPage] = useState<CmsPage>(localized);

  useEffect(() => {
    // Show the language-appropriate inline content immediately, then let a
    // saved CMS override (if any) replace it.
    setPage((fallbacks && fallbacks[lang]) || fallback);
    const params = new URLSearchParams({ lang });
    api.get<CmsPage | null>(`/public/cms-pages/${slug}?${params}`)
      .then(p => { if (p && Array.isArray(p.sections) && p.sections.length > 0) setPage(p); })
      .catch(() => {/* keep fallback */});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, lang]);

  const ACC = 'var(--accent)';
  const mono = 'JetBrains Mono, ui-monospace, monospace';
  const disp = 'Space Grotesk, Inter, system-ui, sans-serif';
  const kicker = slug.startsWith('platform') ? 'LOGIXA · Platform'
    : slug.startsWith('kunden') ? 'LOGIXA · Kunden'
    : (['impressum', 'datenschutz', 'agb'].includes(slug) ? 'LOGIXA · Rechtliches' : 'LOGIXA');

  return (
    <div style={{ minHeight: '100vh', background: 'var(--c-bg)', color: 'var(--c-text)', position: 'relative', overflowX: 'hidden', fontFamily: disp }}>
      <PublicHeader />
      {/* BreadcrumbList structured data — Home > <page> for SERP breadcrumbs */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org', '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'LOGIXA', item: 'https://logixa.one' },
          { '@type': 'ListItem', position: 2, name: page.title },
        ],
      }) }} />
      {/* blueprint grid atmosphere + top glow */}
      <div aria-hidden style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', opacity: 0.4,
        backgroundImage: 'linear-gradient(var(--c-border) 1px,transparent 1px),linear-gradient(90deg,var(--c-border) 1px,transparent 1px)',
        backgroundSize: '60px 60px',
        WebkitMaskImage: 'radial-gradient(ellipse 80% 50% at 50% 0%, #000, transparent 75%)',
        maskImage: 'radial-gradient(ellipse 80% 50% at 50% 0%, #000, transparent 75%)' }} />
      <div aria-hidden style={{ position: 'fixed', inset: '-10% 0 auto 0', height: '52vh', zIndex: 0, pointerEvents: 'none',
        background: 'radial-gradient(55% 50% at 50% 0%, color-mix(in srgb, var(--accent) 11%, transparent), transparent 70%)' }} />

      <div style={{ position: 'relative', zIndex: 1, paddingTop: 64 }}>
        <header style={{ textAlign: 'center', padding: '56px 24px 36px', maxWidth: 900, margin: '0 auto' }}>
          <span style={{ fontFamily: mono, fontSize: 12, fontWeight: 500, letterSpacing: '0.24em', textTransform: 'uppercase', color: ACC, display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <span style={{ width: 26, height: 2, background: ACC, display: 'inline-block' }} />{kicker}
          </span>
          <h1 style={{ fontFamily: disp, fontWeight: 700, fontSize: 'clamp(30px,5vw,52px)', lineHeight: 1.05, letterSpacing: '-0.02em', margin: '18px 0 0', textWrap: 'balance' as any, color: 'var(--c-heading)' }}>{page.title}</h1>
          {page.subtitle && <p style={{ color: 'var(--c-muted)', fontSize: 17, lineHeight: 1.6, maxWidth: '64ch', margin: '16px auto 0' }}>{page.subtitle}</p>}
        </header>

        <main style={{ maxWidth: 900, margin: '0 auto', padding: '20px 24px 72px', display: 'grid', gap: 14 }}>
          {page.sections.map((s, i) => (
            <section key={s.id || i} id={s.id} style={{ position: 'relative', background: 'var(--c-card)', border: '1px solid var(--c-border)', borderRadius: 16, padding: '26px 28px 26px 30px', overflow: 'hidden' }}>
              <span aria-hidden style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: ACC }} />
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 12 }}>
                <span style={{ fontFamily: mono, fontWeight: 700, color: ACC, fontSize: 13 }}>{String(i + 1).padStart(2, '0')}</span>
                <h2 style={{ fontFamily: disp, fontWeight: 600, fontSize: 21, letterSpacing: '-0.01em', margin: 0, color: 'var(--c-heading)' }}>{s.title}</h2>
              </div>
              <div style={{ fontSize: 15, lineHeight: 1.72, color: 'var(--c-text)' }}>
                {renderMarkdown(s.body)}
              </div>
            </section>
          ))}
        </main>

        {page.updatedAt && (
          <p style={{ textAlign: 'center', fontSize: 12, color: 'var(--c-muted)', paddingBottom: 44, fontFamily: mono }}>
            Stand: {new Date(page.updatedAt).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })}
          </p>
        )}
        <PublicFooter />
      </div>
    </div>
  );
}

/**
 * Bare-bones markdown renderer — no library, no dangerouslySetInnerHTML.
 * Supports: paragraphs (\n\n), **bold**, *italic*, [text](url), and
 * `- ` bullet lists. Anything else just renders as plain text. Sufficient
 * for legal pages and the about-us narrative.
 */
function renderMarkdown(md: string): React.ReactNode {
  if (!md) return null;
  const blocks = md.split(/\n{2,}/);
  return blocks.map((block, bi) => {
    const lines = block.split('\n');
    if (lines.every(l => l.trim().startsWith('- '))) {
      return (
        <ul key={bi} className="list-disc list-outside ml-4 space-y-1">
          {lines.map((l, li) => <li key={li}>{inlineMd(l.trim().slice(2))}</li>)}
        </ul>
      );
    }
    return <p key={bi}>{lines.map((l, li) => (
      <span key={li}>{inlineMd(l)}{li < lines.length - 1 && <br />}</span>
    ))}</p>;
  });
}

function inlineMd(text: string): React.ReactNode {
  // Tokens: **bold**, *italic*, [text](url)
  const out: React.ReactNode[] = [];
  let rest = text;
  let key = 0;
  const linkRe = /\[([^\]]+)\]\(([^)]+)\)/;
  const boldRe = /\*\*([^*]+)\*\*/;
  const italRe = /\*([^*]+)\*/;
  while (rest.length > 0) {
    const link = rest.match(linkRe);
    const bold = rest.match(boldRe);
    const ital = rest.match(italRe);
    const candidates = [
      link && { idx: link.index!, len: link[0].length, node: <a key={key++} href={link[2]} target="_blank" rel="noopener noreferrer" className="hover:text-ink underline" style={{ color: 'var(--c-accent)' }}>{link[1]}</a> },
      bold && { idx: bold.index!, len: bold[0].length, node: <strong key={key++} className="text-ink">{bold[1]}</strong> },
      ital && { idx: ital.index!, len: ital[0].length, node: <em key={key++}>{ital[1]}</em> },
    ].filter(Boolean) as Array<{ idx: number; len: number; node: React.ReactNode }>;
    if (candidates.length === 0) { out.push(rest); break; }
    candidates.sort((a, b) => a.idx - b.idx);
    const next = candidates[0];
    if (next.idx > 0) out.push(rest.slice(0, next.idx));
    out.push(next.node);
    rest = rest.slice(next.idx + next.len);
  }
  return out;
}
